/**
 * Planned / billable / actual hours as three labeled values (#2705).
 *
 * Actual is closed net plus open elapsed. Planned and billable are display-only
 * here — this component never writes them.
 */

import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DetailField } from '@/components/common';
import {
  actualExceedsPlanned,
  displayedActualHours,
  displayedUnassignedActualHours,
  formatHoursOrDash,
  hasOpenActualHours,
  type ActualHoursFields,
  type UnassignedActualHoursFields,
} from '@/utils/actualTime';
import { WorkOrderOverrunBadge } from './WorkOrderOverrunBadge';

export interface WorkOrderDurationTrioProps {
  plannedHours: number | null | undefined;
  billableHours: number | null | undefined;
  actual: ActualHoursFields;
  unassigned?: UnassignedActualHoursFields;
  /** `fields` matches the schedule bar; `inline` fits a service-card title. */
  layout?: 'fields' | 'inline';
}

export function WorkOrderDurationTrio({
  plannedHours,
  billableHours,
  actual,
  unassigned,
  layout = 'inline',
}: WorkOrderDurationTrioProps) {
  const { t } = useTranslation('workorders');
  const actualHours = displayedActualHours(actual);
  const exceeds = actualExceedsPlanned(actualHours, plannedHours);
  const running = hasOpenActualHours(actual);
  const actualLabel = running ? t('detail.actualHoursRunning') : t('detail.actualHours');
  const actualValue = formatHoursOrDash(actualHours);
  const unassignedHours = displayedUnassignedActualHours(unassigned);
  const showUnassigned = unassignedHours > 0;

  const actualColor = exceeds ? 'warning.main' : undefined;

  if (layout === 'fields') {
    return (
      <>
        <DetailField
          label={t('detail.plannedHours')}
          value={formatHoursOrDash(plannedHours)}
        />
        <DetailField
          label={t('detail.billableHours')}
          value={formatHoursOrDash(billableHours)}
        />
        <Box data-testid="duration-trio-actual" data-exceeds-planned={exceeds ? 'true' : 'false'}>
          <Typography variant="caption" color="text.secondary">
            {actualLabel}
          </Typography>
          <Typography variant="body2" sx={{ color: actualColor }}>
            {actualValue}
          </Typography>
          {showUnassigned && (
            <Typography variant="caption" color="text.secondary" display="block">
              {t('detail.unassignedActual')}: {formatHoursOrDash(unassignedHours)}
            </Typography>
          )}
          <WorkOrderOverrunBadge
            plannedHours={plannedHours}
            actualDurationHours={actual.actualDurationHours}
          />
        </Box>
      </>
    );
  }

  return (
    <Box data-testid="duration-trio">
      <Typography variant="caption" color="text.secondary" component="div">
        <Box component="span">{t('detail.plannedHours')} {formatHoursOrDash(plannedHours)}</Box>
        {' · '}
        <Box component="span">{t('detail.billableHours')} {formatHoursOrDash(billableHours)}</Box>
        {' · '}
        <Box
          component="span"
          data-testid="duration-trio-actual"
          data-exceeds-planned={exceeds ? 'true' : 'false'}
          sx={{ color: actualColor, fontWeight: exceeds ? 600 : undefined }}
        >
          {actualLabel} {actualValue}
        </Box>
      </Typography>
      {showUnassigned && (
        <Typography variant="caption" color="text.secondary" display="block">
          {t('detail.unassignedActual')}: {formatHoursOrDash(unassignedHours)}
        </Typography>
      )}
      <WorkOrderOverrunBadge
        plannedHours={plannedHours}
        actualDurationHours={actual.actualDurationHours}
      />
    </Box>
  );
}
